import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validationSchemaJointlyContactDetails } from '@/modules/entity/components/individual/jointly/JointlyContactDetails.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { CustomCheckbox, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { PlusIcon, TrashIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

interface JointlyDetails {
  contact_name: string;
  phone_number: string;
  email: string;
  additional_emails: string[];
}

const initialValues: JointlyDetails = {
  contact_name: '',
  phone_number: '',
  email: '',
  additional_emails: [],
};

export const JointlyContactHolderDetails = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const generateInitialValues = (): JointlyDetails => {
    return {
      ...initialValues,
      ...dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly,
      contact_name:
        dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly?.contact_name ??
        `${
          dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly?.first_name ??
          ''
        } ${
          dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly?.last_name ??
          ''
        }`.trim(),
    };
  };

  const formik = useFormik<JointlyDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyContactDetails,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            individual_entity_details: {
              ...dataHolder?.data?.new_data?.entity_data?.individual_entity_details,
              jointly: {
                ...dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly,
                ...values,
              },
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleClickPlusButton = () => {
    if (formik.values.additional_emails) {
      formik.values.additional_emails.length <= 4 &&
        formik.setFieldValue('additional_emails', [...formik.values.additional_emails, '']);
    }
  };

  const handleClickDeleteButton = () => {
    if (!formik.values.additional_emails) return;

    formik.setFieldValue('additional_emails', [
      ...formik.values.additional_emails.slice(0, formik.values.additional_emails.length - 1),
    ]);
  };

  const handleClickAdditionalEmails = (isChecked: boolean) => {
    isChecked
      ? formik.setFieldValue('additional_emails', [''])
      : formik.setFieldValue('additional_emails', []);
  };

  const TrashButton = (
    <button
      key="trash"
      type="button"
      className="absolute inset-y-0 right-0 flex items-center"
      onClick={handleClickDeleteButton}
    >
      <img src={TrashIcon} alt="delete" />
    </button>
  );

  const AddButton = (
    <button
      key="plus"
      type="button"
      className="absolute inset-y-0 right-[40px] flex items-center"
      onClick={handleClickPlusButton}
    >
      <img src={PlusIcon} alt="add" />
    </button>
  );

  return (
    <OnboardingHolderWrapper
      title="Jointly contact details"
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full flex flex-col items-start justify-center space-y-8">
          <TextInputField name="contact_name" label="contact name" />
          <TextInputField
            name="phone_number"
            label="contact telephone number"
            type="tel"
            placeholder="+61 412 345 678"
          />
          <TextInputField name="email" label="primary email address" />
        </div>
        <CustomCheckbox
          label="Add Additional Email Addresses"
          checked={formik.values.additional_emails.length > 0}
          onChange={e => handleClickAdditionalEmails(e.target.checked)}
        />
        {formik.values.additional_emails.length > 0 && (
          <div className="w-full flex flex-col items-start justify-center mt-[45px]">
            <div className="w-full flex flex-col items-start justify-center space-y-1">
              <p className="text-gray-900 text-[14px] leading-[22px]">Additional Email Addresses</p>
              <p className="text-gray-600 text-[12px] leading-5">
                Up to 5 additional email addresses
              </p>
            </div>
            <div className="w-full flex flex-col items-start justify-center space-y-4 mt-6">
              {formik.values?.additional_emails?.map((_, index: number) => {
                return (
                  <TextInputField
                    name={`additional_emails[${index}]`}
                    key={'additional_emails_' + index}
                    buttons={
                      (formik.values.additional_emails &&
                        formik.values.additional_emails.length - 1) === index
                        ? [AddButton, TrashButton]
                        : []
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
        <p className="text-gray-600 text-[12px] leading-5 mt-10">
          By providing your email address,you elect to receive all communications electronically by
          the Compli One share registry
          <br /> (where legallypermissible).
        </p>
      </>
    </OnboardingHolderWrapper>
  );
};
